import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Paper, Box, Typography, Chip, Divider, Stack } from '@mui/material';
import {
  Domain as DomainIcon,
  Psychology as PsychologyIcon,
  Extension as ExtensionIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { getModelById } from './ModelMapping';

const AgentStatusOverview = ({ agentConfig, activeStep }) => {
  const theme = useTheme();
  const steps = [
    {
      label: "Domain & Tasks",
      icon: <DomainIcon />,
      value: agentConfig?.name,
      active: activeStep >= 0,
      completed: !!agentConfig?.name,
      details: agentConfig?.templateId !== 'custom' ? 'Pre-configured template' : 'Custom configuration'
    },
    {
      label: "Intelligence",
      icon: <PsychologyIcon />,
      value: getModelValue(agentConfig),
      active: activeStep >= 1,
      completed: !!agentConfig?.selectedModelId && activeStep > 1,
      details: getIntelligenceDetails(agentConfig)
    },
    {
      label: "Tools & Triggers",
      icon: <ExtensionIcon />,
      value: getToolsValue(agentConfig),
      active: activeStep >= 2,
      completed: activeStep > 2,
      details: agentConfig?.enableLongTermMemory ? "Long-term memory enabled" : null
    },
    {
      label: "Deployment",
      icon: <SecurityIcon />,
      value: agentConfig?.deploymentOption ? agentConfig.deploymentOption === 'cloud' ? 'Cloud' : 'Self-Hosted' : null,
      active: activeStep >= 3,
      completed: !!agentConfig?.deploymentOption && !!agentConfig?.apiType,
      details: agentConfig?.apiType ? `${agentConfig.apiType.toUpperCase()} API` : null
    }
  ];

  function getModelValue(config) {
    if (!config) return null;
    
    // First try to get the selected model's name from ModelMapping
    if (config.selectedModelId) {
      const model = getModelById(config.selectedModelId);
      if (model) {
        return model.name;
      }
    }
    
    // Fallback to selected model name from config
    if (config.selectedModelName) {
      return config.selectedModelName;
    }
    
    // Fallback to model type
    return config.modelType ? config.modelType.toUpperCase() : null;
  }

  function getIntelligenceDetails(config) {
    if (!config) return null;
    
    if (config.selectedModelId) {
      const model = getModelById(config.selectedModelId);
      if (model) {
        return `${model.provider} • ${model.contextWindow} context`;
      }
    }
    
    if (config.modelType) {
      return getModelTypeText(config.modelType);
    }
    
    return null;
  }
  
  function getModelTypeText(modelType) {
    switch(modelType) {
      case 'rag':
        return 'RAG-enabled model';
      case 'standard':
        return 'Standard model';
      case 'fine-tuned':
        return 'Fine-tuned model';
      case 'hybrid':
        return 'Hybrid RAG + fine-tuned';
      case 'base':
        return 'Base model';
      default:
        return null;
    }
  }

  function getToolsValue(config) {
    if (!config) return null;
    
    const toolCount = config.tools?.length || 0;
    const memory = config.shortTermMemory || 'basic';
    
    if (toolCount > 0) {
      return `${toolCount} tools enabled`;
    } else if (config.enableLongTermMemory) {
      return 'Memory enhanced';
    } else {
      return 'Basic configuration';
    }
  }

  return (
    <Paper 
      elevation={1}
      sx={{ 
        py: 2,
        px: 3,
        background: 'linear-gradient(to right, rgba(122, 31, 162, 0.05), rgba(74, 20, 140, 0.1))',
        borderLeft: '4px solid',
        borderColor: theme.palette.secondary.main,
        height: '60px',
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Stack 
        direction="row" 
        spacing={2}
        divider={
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
        }
        sx={{ width: '100%' }}
      >
        {steps.map((step) => (
          <Box 
            key={step.label} 
            sx={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              opacity: step.active ? 1 : 0.7,
              transition: 'all 0.2s ease'
            }}
          >
            <Box sx={{ 
              color: step.completed ? theme.palette.secondary.main : 'text.secondary',
              display: 'flex',
              '& > svg': { fontSize: '1.2rem' }
            }}>
              {step.icon}
            </Box>
            
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography 
                  variant="body2"
                  color="text.secondary"
                >
                  {step.label}:
                </Typography>
                <Typography 
                  variant="body2"
                  sx={{ 
                    fontWeight: step.completed ? 500 : 400,
                    color: step.completed ? 'text.primary' : 'text.disabled',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {step.value || 'Not set'}
                </Typography>
              </Box>
              {step.details && (
                <Typography 
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    fontSize: '0.75rem',
                    lineHeight: 1.2
                  }}
                >
                  {step.details}
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default AgentStatusOverview; 